/**
 * How much room is there?
 *
 * Almost none of this app needs to ask. Every screen is a single column with
 * a max width, 16px gutters and wrapping rows, so it reflows from a desktop
 * to a phone without being told to. Three things could not: the spelling
 * keyboard, whose ten keys have a minimum width; the word map, which is a
 * fixed-shape drawing rather than a flow; and the little "?" panels, which
 * are positioned rather than laid out.
 *
 * Those three ask. Nothing else should — a layout that reflows is more
 * robust than a layout that measures, because it is also right at every
 * width in between.
 */
import { useEffect, useState } from 'react'

/** Below this, treat it as a phone held upright. */
export const NARROW = 560

export function useViewport(): { width: number; height: number; narrow: boolean } {
  const read = () => {
    const width = typeof window === 'undefined' ? NARROW : window.innerWidth
    // Height too, since 8 Oct 2026: the front page's first screen must fit a
    // short laptop window without scrolling, so its picture scales to it.
    const height = typeof window === 'undefined' ? 800 : window.innerHeight
    return { width, height, narrow: width < NARROW }
  }
  const [size, setSize] = useState(read)

  useEffect(() => {
    // Turning a phone sideways fires this, and so does the on-screen keyboard
    // opening on some browsers. Both are real changes of available room.
    const onResize = () => setSize(read())
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  return size
}
